import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { imageUrl } from '../lib/images'

export default function CartDrawer() {
  const {
    cart,
    isCartOpen,
    closeCart,
    removeMenuFromCart,
    removeRentalFromCart,
    updateRentalQuantity,
    clearCart,
    totalItemCount,
    menuTotal,
    rentalsTotal,
    grandTotal,
  } = useCart()

  const [checkoutStep, setCheckoutStep] = useState('overview') // 'overview' | 'checkout'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    event_date: '',
    event_address: '',
    delivery_preference: 'pickup', // 'pickup' | 'delivery'
    message: '',
    website_url: '', // Honeypot field (must be named exactly website_url)
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  })

  const [errors, setErrors] = useState({})

  if (!isCartOpen) return null

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Indtast venligst dit fulde navn'
    if (!formData.phone.trim()) errs.phone = 'Indtast venligst dit telefonnummer'
    if (!formData.email.trim()) {
      errs.email = 'Indtast venligst din e-mailadresse'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.event_date.trim()) {
      errs.event_date = 'Vælg venligst den ønskede dato for festen'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus({ submitting: true, success: false, error: null })

    try {
      // Build detailed overview text
      let overviewText = `=== SAMLET BESTILLINGSFORESPØRGSEL (MENU + UDLEJNING) ===\n\n`

      if (cart.menu) {
        overviewText += `--- 🍽️ CATERING / MENU ---\n`
        overviewText += `Antal kuverter: ${cart.menu.guestCount || 'Ikke angivet'}\n`
        overviewText += `Pris pr. kuvert: ${cart.menu.pricePerGuest} kr.\n`
        overviewText += `Beregnet menu-total: ${cart.menu.totalPrice?.toLocaleString('da-DK')} kr.\n`
        overviewText += `Forret: ${(cart.menu.starters || []).map((s) => s.title).join(', ') || 'Ingen'}\n`
        overviewText += `Kød: ${(cart.menu.meats || []).map((m) => m.title).join(', ') || 'Ingen'}\n`
        overviewText += `Kartofler: ${(cart.menu.potatoes || []).map((p) => p.title).join(', ') || 'Ingen'}\n`
        overviewText += `Salater: ${(cart.menu.salads || []).map((s) => s.title).join(', ') || 'Ingen'}\n\n`
      }

      if (cart.rentals.length > 0) {
        overviewText += `--- 🎪 FESTUDLEJNING ---\n`
        cart.rentals.forEach((r, idx) => {
          const qty = r.quantity || 1
          const unitPrice = Number(r.price) || 0
          const extras = Number(r.extrasCost) || 0
          const lineTotal = unitPrice * qty + extras
          overviewText += `${idx + 1}. ${r.name} (x${qty}) - ${lineTotal.toLocaleString('da-DK')} kr.\n`
          if (r.optionsNote) {
            overviewText += `   Tilvalg/detaljer: ${r.optionsNote}\n`
          }
        })
        overviewText += `Samlet udlejningstotal: ${rentalsTotal.toLocaleString('da-DK')} kr.\n\n`
      }

      overviewText += `=== SAMLET ESTIMERET PRIS: ${grandTotal.toLocaleString('da-DK')} kr. ===\n`
      if (formData.delivery_preference === 'delivery') {
        overviewText += `Ønske om levering/opsætning: Ja (beregnes ud fra adresse)\n`
      } else {
        overviewText += `Ønske om levering/opsætning: Egen afhentning i Sønder Omme\n`
      }

      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        event_date: formData.event_date.trim(),
        event_address: formData.event_address.trim() || 'Ikke oplyst',
        delivery_preference: formData.delivery_preference === 'delivery' ? 'Levering & opsætning ønskes' : 'Afhenter selv i Sønder Omme',
        order_type: cart.menu && cart.rentals.length > 0 ? 'Kombineret (Menu + Udlejning)' : cart.menu ? 'Catering menu' : 'Festudlejning',
        menu_details: cart.menu ? JSON.stringify(cart.menu) : 'Ingen menu valgt',
        rental_details: cart.rentals.length > 0 ? JSON.stringify(cart.rentals) : 'Ingen udlejning valgt',
        estimated_total: `${grandTotal.toLocaleString('da-DK')} kr.`,
        message: `${formData.message.trim()}\n\n${overviewText}`,
        samlet_ordre: overviewText,
        website_url: formData.website_url, // Mandatory honeypot
      }

      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen.')
      }

      setStatus({ submitting: false, success: true, error: null })
      clearCart()
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en uventet fejl. Prøv igen eller ring til os på 25 75 27 24.',
      })
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" aria-labelledby="cart-drawer-title" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        onClick={closeCart}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-lg bg-white shadow-2xl flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 bg-[#FAF8F5] border-b border-[#E6DFD5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C25E27]/10 flex items-center justify-center text-[#C25E27]">
                <i className="fa-solid fa-basket-shopping text-base" aria-hidden="true" />
              </div>
              <div>
                <h2 id="cart-drawer-title" className="text-lg font-bold text-[#241F1C]">
                  Din Festkurv
                </h2>
                <p className="text-xs text-[#6E635C]">
                  {totalItemCount === 0
                    ? 'Kurven er tom'
                    : `${totalItemCount} ${totalItemCount === 1 ? 'del' : 'dele'} til din fest`}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={closeCart}
              className="p-2 text-[#6E635C] hover:text-[#241F1C] hover:bg-white rounded-lg transition-colors cursor-pointer"
              aria-label="Luk kurv"
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {status.success ? (
              <div className="py-12 px-4 text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto text-3xl">
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-extrabold text-[#241F1C]">
                  Mange tak for din forespørgsel!
                </h3>
                <p className="text-sm text-[#5C534D] max-w-sm mx-auto leading-relaxed">
                  Vi har modtaget din samlede bestillingsforespørgsel på både menu og festudstyr.
                  Vi vender hurtigst muligt tilbage med endelig bekræftelse og detaljer.
                </p>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setStatus({ submitting: false, success: false, error: null })
                      setCheckoutStep('overview')
                      closeCart()
                    }}
                    className="bg-[#241F1C] hover:bg-[#38322E] text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-colors cursor-pointer"
                  >
                    Luk kurv
                  </button>
                </div>
              </div>
            ) : totalItemCount === 0 ? (
              <div className="py-16 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#FAF8F5] border border-[#E6DFD5] flex items-center justify-center mx-auto text-[#A89E96] text-2xl">
                  <i className="fa-solid fa-cart-shopping" aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-base text-[#241F1C]">Din kurv er tom lige nu</h3>
                  <p className="text-xs text-[#6E635C] max-w-xs mx-auto">
                    Du kan sammensætte din catering-menu eller tilføje festudlejningsudstyr som softicemaskine og hoppeborg.
                  </p>
                </div>
                <div className="pt-2 flex flex-col gap-2 max-w-xs mx-auto">
                  <Link
                    to="/bestil"
                    onClick={closeCart}
                    className="w-full bg-[#C25E27] hover:bg-[#A84E1C] text-white py-2.5 rounded-lg font-bold text-xs transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-utensils text-xs" aria-hidden="true" />
                    <span>Sammensæt catering menu</span>
                  </Link>
                  <Link
                    to="/fest-og-udlejning"
                    onClick={closeCart}
                    className="w-full bg-[#241F1C] hover:bg-[#38322E] text-white py-2.5 rounded-lg font-bold text-xs transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="fa-solid fa-champagne-glasses text-xs" aria-hidden="true" />
                    <span>Se fest- &amp; udlejningsudstyr</span>
                  </Link>
                </div>
              </div>
            ) : checkoutStep === 'overview' ? (
              <div className="space-y-6">
                {/* 1. Catering Menu Section */}
                {cart.menu && (
                  <div className="border border-[#E6DFD5] rounded-xl p-4 bg-white shadow-xs space-y-3">
                    <div className="flex items-start justify-between gap-3 border-b border-[#E6DFD5] pb-3">
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-[#C25E27]/10 text-[#C25E27] flex items-center justify-center text-xs">
                          <i className="fa-solid fa-utensils" aria-hidden="true" />
                        </span>
                        <div>
                          <h4 className="font-bold text-sm text-[#241F1C]">Catering Menu</h4>
                          <span className="text-xs text-[#6E635C]">
                            {cart.menu.guestCount} kuverter ({cart.menu.pricePerGuest} kr./kuvert)
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeMenuFromCart}
                        className="text-xs text-red-600 hover:text-red-800 p-1 cursor-pointer"
                        title="Fjern menu"
                      >
                        <i className="fa-solid fa-trash-can" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="text-xs text-[#5C534D] space-y-1.5 pl-1">
                      {cart.menu.starters?.length > 0 && (
                        <div>
                          <span className="font-semibold text-[#241F1C]">Forret: </span>
                          {cart.menu.starters.map((s) => s.title).join(', ')}
                        </div>
                      )}
                      {cart.menu.meats?.length > 0 && (
                        <div>
                          <span className="font-semibold text-[#241F1C]">Kød: </span>
                          {cart.menu.meats.map((m) => m.title).join(', ')}
                        </div>
                      )}
                      {cart.menu.potatoes?.length > 0 && (
                        <div>
                          <span className="font-semibold text-[#241F1C]">Kartofler: </span>
                          {cart.menu.potatoes.map((p) => p.title).join(', ')}
                        </div>
                      )}
                      {cart.menu.salads?.length > 0 && (
                        <div>
                          <span className="font-semibold text-[#241F1C]">Salat: </span>
                          {cart.menu.salads.map((s) => s.title).join(', ')}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between items-baseline pt-2 border-t border-[#E6DFD5] text-xs">
                      <Link
                        to="/bestil"
                        onClick={closeCart}
                        className="text-[#C25E27] hover:underline font-medium"
                      >
                        Tilpas i menuguiden
                      </Link>
                      <span className="font-bold text-sm text-[#241F1C]">
                        {cart.menu.totalPrice?.toLocaleString('da-DK')} kr.
                      </span>
                    </div>
                  </div>
                )}

                {/* 2. Festudlejning Section */}
                {cart.rentals.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-[#6E635C] flex items-center gap-1.5">
                        <i className="fa-solid fa-wand-magic-sparkles text-[#C25E27]" aria-hidden="true" />
                        <span>Fest- &amp; udlejningsudstyr</span>
                      </h4>
                      <Link
                        to="/fest-og-udlejning"
                        onClick={closeCart}
                        className="text-xs text-[#C25E27] hover:underline font-medium"
                      >
                        + Tilføj mere
                      </Link>
                    </div>

                    <div className="space-y-3">
                      {cart.rentals.map((item) => {
                        const unitPrice = Number(item.price) || 0
                        const qty = item.quantity || 1
                        const extras = Number(item.extrasCost) || 0
                        const lineTotal = unitPrice * qty + extras

                        return (
                          <div
                            key={item.id}
                            className="border border-[#E6DFD5] rounded-xl p-3.5 bg-white shadow-xs flex gap-3 items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              {item.image && (
                                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#FAF8F5] shrink-0 border border-[#E6DFD5]">
                                  <img
                                    src={imageUrl(item.image, 'width=120,quality=80,format=auto')}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                              <div>
                                <h5 className="font-bold text-sm text-[#241F1C]">{item.name}</h5>
                                {item.optionsNote && (
                                  <p className="text-[11px] text-[#6E635C] leading-snug">
                                    {item.optionsNote}
                                  </p>
                                )}
                                <div className="text-xs font-semibold text-[#C25E27] mt-0.5">
                                  {lineTotal.toLocaleString('da-DK')} kr. inkl. moms
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => removeRentalFromCart(item.id)}
                                className="text-[#A89E96] hover:text-red-600 p-1.5 transition-colors cursor-pointer"
                                title="Fjern fra kurv"
                              >
                                <i className="fa-solid fa-trash-can text-xs" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Additional items teaser */}
                {!cart.menu && (
                  <div className="p-3.5 rounded-xl border border-dashed border-[#C25E27]/40 bg-[#C25E27]/5 flex items-center justify-between gap-3">
                    <div className="text-xs">
                      <span className="font-bold text-[#241F1C] block">Mangler du maden til festen?</span>
                      <span className="text-[#6E635C]">Sammensæt helstegt pattegris eller luksus grillmenu.</span>
                    </div>
                    <Link
                      to="/bestil"
                      onClick={closeCart}
                      className="shrink-0 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                    >
                      Vælg menu
                    </Link>
                  </div>
                )}

                {cart.rentals.length === 0 && (
                  <div className="p-3.5 rounded-xl border border-dashed border-[#241F1C]/30 bg-[#FAF8F5] flex items-center justify-between gap-3">
                    <div className="text-xs">
                      <span className="font-bold text-[#241F1C] block">Mangler du sjov til festen?</span>
                      <span className="text-[#6E635C]">Lej softice, slushice, hoppeborg eller rodeotyr.</span>
                    </div>
                    <Link
                      to="/fest-og-udlejning"
                      onClick={closeCart}
                      className="shrink-0 bg-[#241F1C] hover:bg-[#38322E] text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                    >
                      Se udlejning
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              /* Checkout Form Step */
              <form id="cart-checkout-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-[#E6DFD5]">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep('overview')}
                    className="inline-flex items-center gap-1.5 text-xs text-[#6E635C] hover:text-[#241F1C] font-semibold cursor-pointer"
                  >
                    <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true" />
                    <span>Tilbage til kurvoversigt</span>
                  </button>
                  <span className="text-xs font-bold text-[#C25E27]">
                    Total: {grandTotal.toLocaleString('da-DK')} kr.
                  </span>
                </div>

                <div className="bg-[#FAF8F5] p-3.5 rounded-lg border border-[#E6DFD5] text-xs text-[#5C534D]">
                  <span className="font-bold text-[#241F1C] block mb-0.5">Uforpligtende forespørgsel</span>
                  Dette er ikke en bindende betaling. Vi tjekker datoen og vender hurtigt tilbage med en bekræftelse og detaljer.
                </div>

                {/* Honeypot field */}
                <input
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  className="sr-only absolute -left-[9999px]"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                      Fulde navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="F.eks. Anders Hansen"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full p-2.5 text-xs bg-white border ${
                        errors.name ? 'border-red-500' : 'border-[#E6DFD5]'
                      } rounded-lg focus:outline-none focus:border-[#C25E27]`}
                    />
                    {errors.name && <p className="text-[11px] text-red-500 mt-0.5">{errors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="F.eks. 12 34 56 78"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full p-2.5 text-xs bg-white border ${
                          errors.phone ? 'border-red-500' : 'border-[#E6DFD5]'
                        } rounded-lg focus:outline-none focus:border-[#C25E27]`}
                      />
                      {errors.phone && <p className="text-[11px] text-red-500 mt-0.5">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                        E-mailadresse <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="anders@eksempel.dk"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full p-2.5 text-xs bg-white border ${
                          errors.email ? 'border-red-500' : 'border-[#E6DFD5]'
                        } rounded-lg focus:outline-none focus:border-[#C25E27]`}
                      />
                      {errors.email && <p className="text-[11px] text-red-500 mt-0.5">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                        Dato for arrangement <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="event_date"
                        required
                        value={formData.event_date}
                        onChange={handleChange}
                        className={`w-full p-2.5 text-xs bg-white border ${
                          errors.event_date ? 'border-red-500' : 'border-[#E6DFD5]'
                        } rounded-lg focus:outline-none focus:border-[#C25E27]`}
                      />
                      {errors.event_date && (
                        <p className="text-[11px] text-red-500 mt-0.5">{errors.event_date}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                        Levering eller afhentning?
                      </label>
                      <select
                        name="delivery_preference"
                        value={formData.delivery_preference}
                        onChange={handleChange}
                        className="w-full p-2.5 text-xs bg-white border border-[#E6DFD5] rounded-lg focus:outline-none focus:border-[#C25E27]"
                      >
                        <option value="pickup">Afhenter selv i Sønder Omme (0 kr.)</option>
                        <option value="delivery">Ønsker levering / opsætning</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                      Adresse for festen (hvis levering ønskes)
                    </label>
                    <input
                      type="text"
                      name="event_address"
                      placeholder="Gadenavn, postnr og by"
                      value={formData.event_address}
                      onChange={handleChange}
                      className="w-full p-2.5 text-xs bg-white border border-[#E6DFD5] rounded-lg focus:outline-none focus:border-[#C25E27]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#241F1C] mb-1">
                      Eventuelle bemærkninger eller ønsker
                    </label>
                    <textarea
                      name="message"
                      rows={3}
                      placeholder="Skriv gerne særlige tidspunkter, ønsker om lejeperiode eller spørgsmål..."
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full p-2.5 text-xs bg-white border border-[#E6DFD5] rounded-lg focus:outline-none focus:border-[#C25E27]"
                    />
                  </div>

                  {status.error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">
                      {status.error}
                    </div>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* Footer actions */}
          {!status.success && totalItemCount > 0 && (
            <div className="p-6 bg-[#FAF8F5] border-t border-[#E6DFD5] space-y-4">
              <div className="space-y-1.5 text-xs">
                {cart.menu && (
                  <div className="flex justify-between text-[#5C534D]">
                    <span>Catering menu ({cart.menu.guestCount} kuverter):</span>
                    <span className="font-semibold text-[#241F1C]">{menuTotal.toLocaleString('da-DK')} kr.</span>
                  </div>
                )}
                {cart.rentals.length > 0 && (
                  <div className="flex justify-between text-[#5C534D]">
                    <span>Festudlejning i alt:</span>
                    <span className="font-semibold text-[#241F1C]">{rentalsTotal.toLocaleString('da-DK')} kr.</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-2 border-t border-[#E6DFD5]">
                  <span className="text-sm font-bold text-[#241F1C]">Samlet estimeret pris:</span>
                  <span className="text-xl font-black text-[#C25E27]">
                    {grandTotal.toLocaleString('da-DK')} kr.
                  </span>
                </div>
              </div>

              {checkoutStep === 'overview' ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setCheckoutStep('checkout')}
                    className="w-full bg-[#C25E27] hover:bg-[#A84E1C] text-white py-3 rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>Gå til samlet bestilling / forespørgsel</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={clearCart}
                    className="w-full text-center text-xs text-[#A89E96] hover:text-[#C25E27] py-1 cursor-pointer transition-colors"
                  >
                    Tøm kurv
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    type="submit"
                    form="cart-checkout-form"
                    disabled={status.submitting}
                    className="w-full bg-[#C25E27] hover:bg-[#A84E1C] disabled:bg-[#A89E96] text-white py-3 rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {status.submitting ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" />
                        <span>Sender samlet forespørgsel...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        <span>Send samlet bestillingsforespørgsel</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
